import { useEffect, useState } from 'react';
import { isNativeApp, nativeApp } from '@/features/location/native-bridge';

/**
 * What the server says the newest shell is.
 *
 * Served from this site's own origin as a static file, not from the API: the
 * page's CSP is `connect-src 'self'`, so a fetch to the host the APK actually
 * lives on would be blocked. The manifest is same-origin and cheap; only the
 * download crosses hosts, and that is a navigation rather than a fetch.
 */
export interface AppRelease {
  /** Android versionCode. The only field worth comparing — versionName is prose. */
  versionCode: number;
  versionName: string;
  /**
   * Where the APK is.
   *
   * Deliberately NOT on this host. Capacitor keeps same-host navigation inside
   * the WebView, and the WebView has no DownloadListener, so an APK link on
   * hr.kiobytes.com does nothing at all. A different host makes the bridge fire
   * ACTION_VIEW and hand the URL to the system browser, which can download it.
   */
  url: string;
  sizeBytes?: number;
  notes?: string;
}

export interface AppUpdate {
  /** Null in a browser, or until the check finishes. */
  installed: { versionName: string; versionCode: number } | null;
  available: AppRelease | null;
}

/**
 * Whether a newer shell than the one running is published.
 *
 * Android cannot install a sideloaded APK without the person confirming it —
 * that is not a gap to work around, it is the platform refusing to let an app
 * replace itself silently. So this offers the download and stops. What it
 * removes is the part that actually hurt: finding the file, getting it onto the
 * handset, and knowing a new one exists at all.
 *
 * Returns nothing in a browser, where "update the app" is meaningless.
 */
export function useAppUpdate(): AppUpdate {
  const [installed, setInstalled] = useState<AppUpdate['installed']>(null);
  const [available, setAvailable] = useState<AppRelease | null>(null);

  useEffect(() => {
    if (!isNativeApp()) return;

    const plugin = nativeApp();
    if (!plugin?.getInfo) return;

    let cancelled = false;

    void (async () => {
      try {
        const info = await plugin.getInfo!();
        // `build` is the versionCode, as a string.
        const versionCode = Number.parseInt(info.build, 10);
        if (cancelled || !Number.isFinite(versionCode)) return;

        setInstalled({ versionName: info.version, versionCode });

        // no-store: this file is the whole point of the check, and a cached
        // copy from before the last release answers the wrong question.
        const response = await fetch('/app-version.json', { cache: 'no-store' });
        if (!response.ok || cancelled) return;

        const release = (await response.json()) as AppRelease;
        if (cancelled) return;

        if (
          typeof release?.versionCode === 'number' &&
          typeof release?.url === 'string' &&
          release.versionCode > versionCode
        ) {
          setAvailable(release);
        }
      } catch {
        // Offline, an older shell without getInfo, or no manifest published.
        // An update prompt is not worth an error message — the app works.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return { installed, available };
}
